import { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { LoadingButton } from '@mui/lab';
import {
  Dialog,
  DialogContent,
  DialogActions,
  DialogTitle,
  Button,
  Grid,
  MenuItem,
  FormControl,
  Box,
  IconButton,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { useForm, FormProvider } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import Preloader from 'components/Preloader';
import CustomTextInput from 'components/Forms/CustomTextInput';
import CustomCheckbox from 'components/Forms/CustomCheckbox';
import LabelRequired from 'components/FormFields/LabelRequired';
import DraggableDialogWrapper from 'components/DraggableDialogWrapper/DraggableDialogWrapper';
import { selectCurrentBusiness } from 'redux/selectors';
import { DEFAULT_TIMEZONE } from 'components/Chargeback/constants';
import CouponFields from 'pages/PosSettings/Coupons/CouponFields';
import {
  couponMarketingValue,
  couponSchemaFields,
  couponToFormValues,
} from 'pages/PosSettings/Coupons/couponForm';
import PromotionActionFields from 'pages/PosSettings/Promotions/PromotionActionFields';
import ChannelSelect from 'pages/PosSettings/ChannelSelect';
import { channelsToFormValue } from 'pages/PosSettings/channels';
import {
  PROMOTION_TYPES,
  actionSchemaFields,
  actionDefaultValues,
  actionToFormValues,
  applyPromotionType,
} from 'pages/PosSettings/Promotions/promotionAction';

const CustomDialogTitle = ({ children, onClose, ...other }) => (
  <DialogTitle sx={{ m: 0, p: 2 }} {...other}>
    {children}
    {onClose ? (
      <IconButton
        aria-label="close"
        onClick={onClose}
        sx={{
          position: 'absolute',
          right: 8,
          top: 8,
          color: (theme) => theme.palette.grey[500],
        }}
      >
        <CloseIcon />
      </IconButton>
    ) : null}
  </DialogTitle>
);

CustomDialogTitle.propTypes = {
  children: PropTypes.node,
  onClose: PropTypes.func.isRequired,
};

const SectionTitle = ({ children }) => (
  <Typography variant="subtitle2" sx={{ fontWeight: 600, mt: 1 }}>
    {children}
  </Typography>
);

const AddEditModal = ({
  data = null,
  productOptions = [],
  categoryOptions = [],
  showModal = false,
  loading = false,
  onConfirm = () => {},
  onCancel = () => {},
}) => {
  const { t } = useTranslation();
  const currentBusiness = useSelector(selectCurrentBusiness);
  const timezoneId = currentBusiness?.timezone?.timeZoneId || DEFAULT_TIMEZONE;

  dayjs.extend(utc);
  dayjs.extend(timezone);

  const editMode = useMemo(() => Boolean(data?.uuid), [data]);

  const [startDate, setStartDate] = useState(
    data?.startAt
      ? new Date(data.startAt)
      : dayjs(new Date()).tz(timezoneId)['$d']
  );
  const [endDate, setEndDate] = useState(
    data?.endAt
      ? new Date(data.endAt)
      : dayjs(new Date()).tz(timezoneId).add(1, 'year')['$d']
  );

  const formSchema = yup.object().shape({
    promotionName: yup.string().required('field_is_required_'),
    type: yup.string().required('field_is_required_'),
    channels: yup.array().of(yup.string()).nullable(),
    ...actionSchemaFields,
    ...couponSchemaFields(),
  });

  const formMethods = useForm({
    mode: 'all',
    resolver: yupResolver(formSchema),
    defaultValues: {
      promotionName:
        data?.promotionName === '—' ? '' : data?.promotionName || '',
      type: data?.promotionType || '',
      channels: channelsToFormValue(data?.channels),
      ...(data?.action ? actionToFormValues(data.action) : actionDefaultValues),
      ...couponToFormValues(data?._raw),
      useForMarketing: couponMarketingValue(data?._raw),
      active: data ? data.status === 'ACTIVE' : true,
    },
  });

  const promotionType = formMethods.watch('type');

  const handleSubmit = (values) =>
    onConfirm({ values, startDate, endDate, editing: data });

  return (
    <Dialog
      open={showModal}
      disableEscapeKeyDown={true}
      onClose={() => {}}
      fullWidth={true}
      PaperComponent={DraggableDialogWrapper}
      PaperProps={{
        sx: { width: '763px', maxWidth: '100%', background: '#FAFAFA', p: 2 },
      }}
    >
      {loading && <Preloader />}
      <FormProvider {...formMethods}>
        <form onSubmit={formMethods.handleSubmit(handleSubmit)}>
          <CustomDialogTitle id="pos-coupon-dialog-title" onClose={onCancel}>
            {editMode ? t('Update the coupon') : t('Add new coupon')}
          </CustomDialogTitle>
          <DialogContent sx={{ width: '100%' }}>
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12}>
                <SectionTitle>{t('Promotion')}</SectionTitle>
              </Grid>
              <Grid item xs={12} sx={{ height: '72px' }}>
                <CustomTextInput
                  label={<LabelRequired>{t('Promotion name')}</LabelRequired>}
                  name="promotionName"
                  sx={{ width: '100%' }}
                />
              </Grid>
              <Grid item xs={12} sx={{ height: '72px' }}>
                <CustomTextInput
                  select={true}
                  name="type"
                  label={
                    <LabelRequired>
                      {t('website_settings.discount_type')}
                    </LabelRequired>
                  }
                  sx={{ width: '100%', mt: 1 }}
                  onChange={(e) =>
                    applyPromotionType(formMethods.setValue, e.target.value)
                  }
                >
                  {PROMOTION_TYPES.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {t(option.label)}
                    </MenuItem>
                  ))}
                </CustomTextInput>
              </Grid>
              <ChannelSelect xs={12} />
              <PromotionActionFields
                type={promotionType}
                productOptions={productOptions}
                categoryOptions={categoryOptions}
              />

              <Grid item xs={12}>
                <SectionTitle>{t('Coupon Code')}</SectionTitle>
              </Grid>
              <CouponFields />

              <Grid item xs={12} sx={{ height: '72px' }}>
                <LocalizationProvider dateAdapter={AdapterDateFns}>
                  <Box sx={{ display: 'flex', gap: '16px' }}>
                    <FormControl sx={{ width: '50%' }}>
                      <DatePicker
                        sx={{
                          width: '100%',
                          input: { padding: '8px 0 8px 12px' },
                        }}
                        name="start_date"
                        value={startDate}
                        onChange={setStartDate}
                        label={
                          <span style={{ backgroundColor: '#fafafa' }}>
                            {t('website_settings.start_date')}
                          </span>
                        }
                      />
                    </FormControl>
                    <FormControl sx={{ width: '50%' }}>
                      <DatePicker
                        sx={{
                          width: '100%',
                          input: { padding: '8px 0 8px 12px' },
                        }}
                        name="end_date"
                        value={endDate}
                        onChange={setEndDate}
                        label={
                          <span style={{ backgroundColor: '#fafafa' }}>
                            {t('website_settings.end_date')}
                          </span>
                        }
                      />
                    </FormControl>
                  </Box>
                </LocalizationProvider>
              </Grid>

              <Grid item xs={12}>
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    width: 'max-content',
                    '>label': { width: 'max-content' },
                  }}
                >
                  <CustomCheckbox
                    sx={{ '& .MuiSvgIcon-root': { fontSize: 20 } }}
                    label={t('settings.active')}
                    name="active"
                  />
                  <CustomCheckbox
                    sx={{ '& .MuiSvgIcon-root': { fontSize: 20 } }}
                    label={t(
                      'website_settings.available_for_marketing_campaign'
                    )}
                    name="useForMarketing"
                  />
                </Box>
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions sx={{ cursor: 'grab' }}>
            <Button
              variant="text"
              sx={{ mr: 2, color: 'black' }}
              color="primary"
              onClick={onCancel}
            >
              {t('orders.cancel')}
            </Button>
            <LoadingButton
              type="submit"
              variant="contained"
              loading={loading}
              disabled={loading}
            >
              {editMode
                ? t('website_settings.update')
                : t('settings.save')}
            </LoadingButton>
          </DialogActions>
        </form>
      </FormProvider>
    </Dialog>
  );
};

AddEditModal.propTypes = {
  showModal: PropTypes.bool.isRequired,
  onConfirm: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
};

export default AddEditModal;
